import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import type { RootState } from "@/app/store/store";
import { STATUS } from "@/constants";
import { apiFetch } from "@/shared/api/apiFetch";
import { readApiErrorMessage } from "@/shared/api/parseApiErrorMessage";

/**
 * A PDF stored under the domain's prefix in the tenant's S3 bucket. There is no database row behind
 * this — the backend derives every field from S3 on each read, so `added_at` / `last_modified_at`
 * are the bucket's own timestamps.
 */
export type DomainDocument = {
  filename: string;
  size_bytes: number;
  added_at: string;
  last_modified_at: string;
};

export type UploadDomainDocumentPayload = {
  domainId: number;
  file: File;
};

export type DeleteDomainDocumentPayload = {
  domainId: number;
  filename: string;
};

export type DomainDocumentsState = {
  status: "idle" | "loading" | "succeeded" | "failed";
  error: string | null;
  documentsByDomainId: Record<number, DomainDocument[]>;
};

export const domainDocumentsInitialState: DomainDocumentsState = {
  status: STATUS.IDLE,
  error: null,
  documentsByDomainId: {},
};

function isDomainDocument(value: unknown): value is DomainDocument {
  if (typeof value !== "object" || value === null) {
    return false;
  }
  const row = value as Record<string, unknown>;
  return (
    typeof row.filename === "string" &&
    typeof row.size_bytes === "number" &&
    typeof row.added_at === "string" &&
    typeof row.last_modified_at === "string"
  );
}

function parseDomainDocumentListResponse(data: unknown): DomainDocument[] | null {
  if (!Array.isArray(data)) {
    return null;
  }
  if (!data.every(isDomainDocument)) {
    return null;
  }
  return data;
}

function sortByFilename(documents: DomainDocument[]): DomainDocument[] {
  return [...documents].sort((left, right) =>
    left.filename.localeCompare(right.filename, undefined, { sensitivity: "base" }),
  );
}

export const fetchDomainDocuments = createAsyncThunk<
  { domainId: number; documents: DomainDocument[] },
  number,
  { rejectValue: string }
>("domainDocuments/fetch", async (domainId, { rejectWithValue }) => {
  try {
    const res = await apiFetch(`/v1/domains/${domainId}/documents`, { method: "GET" });
    if (!res.ok) {
      return rejectWithValue(await readApiErrorMessage(res));
    }
    const data: unknown = await res.json();
    const documents = parseDomainDocumentListResponse(data);
    if (!documents) {
      return rejectWithValue("Unexpected response shape");
    }
    return { domainId, documents };
  } catch (error) {
    const message = error instanceof Error ? error.message : "Request failed";
    return rejectWithValue(message);
  }
});

/**
 * Uploads exactly one file. Callers upload a batch by dispatching this per file, so a file the
 * server rejects fails in isolation and its siblings still land.
 */
export const uploadDomainDocument = createAsyncThunk<
  { domainId: number; document: DomainDocument },
  UploadDomainDocumentPayload,
  { rejectValue: string }
>("domainDocuments/upload", async ({ domainId, file }, { rejectWithValue }) => {
  try {
    const body = new FormData();
    body.append("file", file, file.name);
    // No explicit Content-Type: the browser has to set the multipart boundary itself.
    const res = await apiFetch(`/v1/domains/${domainId}/documents`, { method: "POST", body });
    if (!res.ok) {
      return rejectWithValue(await readApiErrorMessage(res));
    }
    const data: unknown = await res.json();
    if (!isDomainDocument(data)) {
      return rejectWithValue("Unexpected response shape");
    }
    return { domainId, document: data };
  } catch (error) {
    const message = error instanceof Error ? error.message : "Request failed";
    return rejectWithValue(message);
  }
});

export const deleteDomainDocument = createAsyncThunk<
  DeleteDomainDocumentPayload,
  DeleteDomainDocumentPayload,
  { rejectValue: string }
>("domainDocuments/delete", async (payload, { rejectWithValue }) => {
  try {
    const res = await apiFetch(
      `/v1/domains/${payload.domainId}/documents/${encodeURIComponent(payload.filename)}`,
      { method: "DELETE" },
    );
    if (!res.ok) {
      return rejectWithValue(await readApiErrorMessage(res));
    }
    return payload;
  } catch (error) {
    const message = error instanceof Error ? error.message : "Request failed";
    return rejectWithValue(message);
  }
});

const domainDocumentsSlice = createSlice({
  name: "domainDocuments",
  initialState: domainDocumentsInitialState,
  reducers: {},
  extraReducers(builder) {
    builder
      .addCase(fetchDomainDocuments.pending, (state) => {
        state.status = STATUS.LOADING;
        state.error = null;
      })
      .addCase(fetchDomainDocuments.fulfilled, (state, action) => {
        state.status = STATUS.SUCCEEDED;
        state.error = null;
        state.documentsByDomainId[action.payload.domainId] = action.payload.documents;
      })
      .addCase(fetchDomainDocuments.rejected, (state, action) => {
        state.status = STATUS.FAILED;
        state.error = action.payload ?? "Request failed";
      })
      .addCase(uploadDomainDocument.fulfilled, (state, action) => {
        const { domainId, document } = action.payload;
        const existing = state.documentsByDomainId[domainId] ?? [];
        // Replacing an existing name keeps its position and its `added_at`; only the row's
        // `last_modified_at` and size move, which mirrors what S3 just did to the object.
        const withoutReplaced = existing.filter((row) => row.filename !== document.filename);
        state.documentsByDomainId[domainId] = sortByFilename([...withoutReplaced, document]);
      })
      .addCase(deleteDomainDocument.fulfilled, (state, action) => {
        const { domainId, filename } = action.payload;
        const existing = state.documentsByDomainId[domainId];
        if (!existing) {
          return;
        }
        state.documentsByDomainId[domainId] = existing.filter(
          (document) => document.filename !== filename,
        );
      });
  },
});

export const domainDocumentsReducer = domainDocumentsSlice.reducer;

export function selectDomainDocumentsForDomain(domainId: number | null) {
  return (state: RootState): DomainDocument[] =>
    domainId === null ? [] : (state.domainDocuments.documentsByDomainId[domainId] ?? []);
}

export const selectDomainDocumentsStatus = (state: RootState) => state.domainDocuments.status;

export const selectDomainDocumentsError = (state: RootState) => state.domainDocuments.error;
